/**
 * Shared polling loop for asynchronous "report is being generated" style
 * backends. Callers provide an `attempt` function that performs the actual
 * request and reports whether the result is ready via `done`. Any other
 * error thrown by `attempt` propagates immediately (not treated as pending).
 *
 * `REPORT_POLL_DELAYS_MS`, `MAX_POLL_ATTEMPTS` and `MAX_POLL_DURATION_MS`
 * are the shared defaults used by all report-polling endpoints in this app.
 * Callers with a genuinely different SLA may still pass their own values.
 */

// Fixed backoff sequence between poll attempts, capped at the last value.
export const REPORT_POLL_DELAYS_MS = [1000, 2000, 4000, 5000];
export const MAX_POLL_ATTEMPTS = 12;
export const MAX_POLL_DURATION_MS = 60_000;

export class PollTimeoutError extends Error {
  constructor(message = "Polling timed out") {
    super(message);
    this.name = "PollTimeoutError";
  }
}

export interface PollAttemptResult<T> {
  done: boolean;
  value?: T;
  /** Optional override for the next delay, e.g. derived from a Retry-After header. */
  nextDelayMs?: number;
}

export interface PollWithBackoffOptions<T> {
  attempt: (completedAttempts: number) => Promise<PollAttemptResult<T>>;
  delaysMs?: number[];
  maxAttempts?: number;
  maxDurationMs?: number;
  wait?: (delayMs: number, signal?: AbortSignal) => Promise<void>;
  now?: () => number;
  signal?: AbortSignal;
}

export const getPollDelayMs = (
  completedAttempt: number,
  delaysMs: number[] = REPORT_POLL_DELAYS_MS
): number => delaysMs[Math.min(completedAttempt - 1, delaysMs.length - 1)];

const defaultWait = (delayMs: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      const error = new Error("Polling was aborted");
      error.name = "AbortError";
      reject(error);
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }

    timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Polls `attempt` sequentially with backoff until it reports `done`, the
 * maximum number of attempts is exhausted, or the deadline (`maxDurationMs`
 * from the first call) is reached. Throws {@link PollTimeoutError} in the
 * latter two cases.
 *
 * Waits before each attempt (including the first), using `delaysMs[0]` as
 * the initial delay. This mirrors polling backends that need a moment
 * before the resource is expected to exist.
 */
export const pollWithBackoff = async <T>({
  attempt,
  delaysMs = REPORT_POLL_DELAYS_MS,
  maxAttempts = MAX_POLL_ATTEMPTS,
  maxDurationMs = MAX_POLL_DURATION_MS,
  wait = defaultWait,
  now = Date.now,
  signal,
}: PollWithBackoffOptions<T>): Promise<T> => {
  const deadline = now() + maxDurationMs;
  let completedAttempts = 0;
  let delayMs = delaysMs[0] ?? 1000;

  /* eslint-disable no-await-in-loop */
  while (completedAttempts < maxAttempts) {
    const remainingMs = deadline - now();
    if (remainingMs <= 0) throw new PollTimeoutError();

    // Polling is intentionally sequential: each attempt depends on the
    // previous pending response.
    await wait(Math.min(delayMs, remainingMs), signal);
    if (now() >= deadline) throw new PollTimeoutError();

    const result = await attempt(completedAttempts);
    if (result.done) return result.value as T;

    completedAttempts += 1;
    if (completedAttempts >= maxAttempts) break;

    delayMs = result.nextDelayMs ?? getPollDelayMs(completedAttempts, delaysMs);
  }
  /* eslint-enable no-await-in-loop */

  throw new PollTimeoutError();
};
